import type { BusinessDay, CashDrawerMovement, DayOpsState } from '@/types'

/**
 * Plain-English label and tone for the current day's lifecycle state.
 * Used by the page hero, the sidebar status pill, and the empty-state
 * cards so the same simple vocabulary appears everywhere. Note: the
 * 'staged' state is an internal back-end step; the UI presents it to
 * users as "almost done" since they will not have triggered it without
 * counting cash first.
 */
export function dayStateLabel(state: DayOpsState | undefined | null): {
  label: string
  shortLabel: string
  tone: 'open' | 'staged' | 'closed' | 'none'
  description: string
} {
  switch (state) {
    case 'open':
      return {
        label: 'Today is open',
        shortLabel: 'Open',
        tone: 'open',
        description: "Sales today are being recorded. End the day when your shift is over.",
      }
    case 'staged':
      return {
        label: 'Almost done — one more tap',
        shortLabel: 'Almost done',
        tone: 'staged',
        description:
          "You've counted the cash. Tap Finish the day to lock today's numbers.",
      }
    case 'closed':
      return {
        label: 'Today is finished and locked',
        shortLabel: 'Locked',
        tone: 'closed',
        description:
          "Today's numbers are saved. Only the owner can change them now.",
      }
    case 'reopened':
      return {
        label: 'Day reopened',
        shortLabel: 'Reopened',
        tone: 'open',
        description:
          "A sale was added after seal. The day is open again — re-run End the day to lock fresh numbers.",
      }
    default:
      return {
        label: 'Ready to start the day',
        shortLabel: 'Not started',
        tone: 'none',
        description:
          "Tap Start the day when the shop opens. We'll keep track of every sale until you finish.",
      }
  }
}

/** Sums paid_in − paid_out so the wizard can show expected cash. */
export function netCashAdjustments(movements: CashDrawerMovement[] | undefined): number {
  if (!movements?.length) return 0
  return movements.reduce((acc, m) => {
    return m.movement_type === 'paid_in' ? acc + m.amount : acc - m.amount
  }, 0)
}

/**
 * Expected cash in the drawer = opening float + cash collected in sales
 * + paid-in adjustments − paid-out adjustments.
 *
 * Defensive against undefined fields so partially-loaded snapshots
 * still render something sensible.
 */
export function expectedCash({
  day,
  cashSales,
  movements,
}: {
  day: BusinessDay | null | undefined
  cashSales: number
  movements: CashDrawerMovement[] | undefined
}): number {
  if (!day) return 0
  return (day.opening_cash ?? 0) + cashSales + netCashAdjustments(movements)
}

/**
 * Human-readable label for a single audit row, mirroring the action
 * vocabulary defined in dayops.Service.writeAuditTx.
 */
export function auditActionLabel(action: string): string {
  switch (action) {
    case 'open_day':
      return 'Day started'
    case 'auto_open_day':
      return 'Day started automatically'
    case 'cash_movement':
      return 'Money added or taken out'
    case 'stage_close':
      return 'Cash counted'
    case 'lock_close':
      return 'Day finished and locked'
    case 'reopen':
      return 'Day unlocked'
    case 'reopen_for_sale':
      return 'Reopened by late sale'
    case 'auto_finalize':
      return 'Auto-finalized at closeout hour'
    case 'force_close_no_count':
      return 'Force-closed without count'
    case 'session_elevate':
      return 'Manager signed in via PIN'
    default:
      return action
  }
}

/**
 * Count consecutive days the owner has closed the day on time. Filters
 * to closed rows only (so today's open/staged/reopened state doesn't
 * zero out the historical chain), sorts descending, then walks looking
 * for a run of consecutive calendar dates ending at the most recent
 * closed day. Returns 0 when no closed history exists.
 */
export function computeClosingStreak(history: BusinessDay[] | undefined | null): number {
  if (!history?.length) return 0
  const closed = history
    .filter((d) => d.status === 'closed')
    .sort((a, b) => (b.business_date < a.business_date ? -1 : b.business_date > a.business_date ? 1 : 0))
  if (closed.length === 0) return 0
  let streak = 1
  let expected = previousISODate(closed[0].business_date)
  for (let i = 1; i < closed.length; i++) {
    if (closed[i].business_date !== expected) break
    streak += 1
    expected = previousISODate(closed[i].business_date)
  }
  return streak
}

/** Returns yyyy-MM-dd for the day before the given ISO date. */
function previousISODate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() - 1)
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/** Returns yyyy-MM-dd for the day after the given ISO date. */
function nextISODate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + 1)
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

function localISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/**
 * Longest run of consecutive closed days within the lookback window.
 * Used by the DayHistoryPage metrics block to give the owner a sense of
 * peak consistency over time.
 */
export function computeLongestStreak(
  history: BusinessDay[] | undefined | null,
  lookbackDays = 90,
): number {
  if (!history?.length) return 0
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const cutoff = new Date(today)
  cutoff.setDate(cutoff.getDate() - lookbackDays)
  const cutoffISO = localISODate(cutoff)

  const closed = history
    .filter((d) => d.status === 'closed' && d.business_date >= cutoffISO)
    .sort((a, b) => (a.business_date < b.business_date ? -1 : a.business_date > b.business_date ? 1 : 0))

  if (closed.length === 0) return 0
  let longest = 1
  let current = 1
  for (let i = 1; i < closed.length; i++) {
    if (closed[i].business_date === nextISODate(closed[i - 1].business_date)) {
      current += 1
      if (current > longest) longest = current
    } else {
      current = 1
    }
  }
  return longest
}

/**
 * Calendar days from the 1st of the current month up to (and including)
 * yesterday that don't have a 'closed' business_days row. Today is
 * excluded because there's still time to close. Holidays / scheduled
 * closures aren't modeled today — a Sunday-off shop will see those
 * Sundays counted as "missed" until a calendar config exists.
 */
export function computeMissedDaysThisMonth(
  history: BusinessDay[] | undefined | null,
): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (yesterday < firstOfMonth) return 0

  const closedDates = new Set(
    (history ?? [])
      .filter((d) => d.status === 'closed')
      .map((d) => d.business_date),
  )

  let missed = 0
  const cursor = new Date(firstOfMonth)
  while (cursor <= yesterday) {
    if (!closedDates.has(localISODate(cursor))) missed += 1
    cursor.setDate(cursor.getDate() + 1)
  }
  return missed
}

/**
 * One row in the Business Days table. Real rows wrap a BusinessDay; the
 * synthetic 'missed' rows are dates the calendar fills in so the table
 * shows every day of the month, not just the ones that landed in the
 * `business_days` table.
 */
export type MonthDisplayRow =
  | { kind: 'day'; day: BusinessDay }
  | { kind: 'missed'; date: string }

/**
 * Builds the row list for the month-paginated Business Days table —
 * real business_days merged with synthetic 'missed' rows for every
 * calendar date in [floor, ceiling] that has no record.
 *
 * Floor   = max(first-of-month, earliestDate). Days before the system
 *           started are hidden — otherwise the very first month would
 *           render ~30 spurious "missed" rows.
 * Ceiling = min(last-of-month, today). Future days never appear.
 *
 * Today itself is never synthesized as 'missed' even if it has no row —
 * the admin still has time to open the day. Same rule
 * computeMissedDaysThisMonth uses (stops at yesterday).
 *
 * Output is newest-first to match the existing table order.
 */
export function buildMonthDisplayRows(args: {
  month: string
  history: BusinessDay[]
  earliestDate: string | null
  today: Date
}): MonthDisplayRow[] {
  const { month, history, earliestDate, today } = args
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return []

  const firstOfMonth = new Date(y, m - 1, 1)
  const lastOfMonth = new Date(y, m, 0)
  const todayMidnight = new Date(today)
  todayMidnight.setHours(0, 0, 0, 0)
  const todayISO = localISODate(todayMidnight)

  let floor = firstOfMonth
  if (earliestDate) {
    const [ey, em, ed] = earliestDate.split('-').map(Number)
    if (ey && em && ed) {
      const earliest = new Date(ey, em - 1, ed)
      if (earliest > floor) floor = earliest
    }
  }
  const ceiling = todayMidnight < lastOfMonth ? todayMidnight : lastOfMonth
  if (floor > ceiling) return []

  const byDate = new Map(history.map((d) => [d.business_date, d]))
  const out: MonthDisplayRow[] = []
  const cursor = new Date(ceiling)
  while (cursor >= floor) {
    const iso = localISODate(cursor)
    const real = byDate.get(iso)
    if (real) {
      out.push({ kind: 'day', day: real })
    } else if (iso < todayISO) {
      out.push({ kind: 'missed', date: iso })
    }
    cursor.setDate(cursor.getDate() - 1)
  }
  return out
}

/**
 * Closed days within the lookback window that were finalized without a
 * manager — `closed_by` is NULL, which is the signature the
 * AutoFinalizeStaleDays sweeper leaves behind when it locks a forgotten
 * day at the closeout hour.
 */
export function computeAutoFinalizedCount(
  history: BusinessDay[] | undefined | null,
  lookbackDays = 30,
): number {
  if (!history?.length) return 0
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const cutoff = new Date(today)
  cutoff.setDate(cutoff.getDate() - lookbackDays)
  const cutoffISO = localISODate(cutoff)
  return history.filter(
    (d) => d.status === 'closed' && d.business_date >= cutoffISO && !d.closed_by,
  ).length
}
